import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiAlertCircle } from 'react-icons/fi';
import { authService } from '../services/authService';
import { redirectToGoogleOAuth } from '../services/oauth';
import GoogleIcon from '../components/GoogleIcon';
import PaperboyWordmark from '../components/PaperboyWordmark';
import './AuthPages.css';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [isMagicLinkLoading, setIsMagicLinkLoading] = useState(false);

  const { login, error, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  useEffect(() => {
    if (localError || error) {
      const timer = setTimeout(() => {
        setLocalError('');
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [localError, error, clearError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    const result = await login(email, password);
    setIsLoading(false);

    if (result.success) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  };

  const isEmailValid = email.length > 0 && /\S+@\S+\.\S+/.test(email);

  const handleMagicLink = async () => {
    if (!isEmailValid || isMagicLinkLoading) return;
    setIsMagicLinkLoading(true);
    setLocalError('');
    try {
      await authService.requestMagicLink(email);
      setMagicLinkSent(true);
    } catch {
      setLocalError('Could not send magic link. Try again.');
    } finally {
      setIsMagicLinkLoading(false);
    }
  };

  const displayError = localError || error;

  return (
    <div className="auth-page auth-page--login">
      <div className="auth-card">
        <div className="auth-card__top">
          <div className="login-header">
            <div className="login-header__wordmark">
              <Link to="/"><PaperboyWordmark size={22} /></Link>
            </div>
            <div className="login-header__eyebrow">
              <span className="mono-tag">RETURNING READER</span>
            </div>
            <h1 className="login-header__heading">Reader,<br />not feed.</h1>
            <p className="login-header__tagline">Sign in to your front page.</p>
          </div>
        </div>

        {displayError && (
          <div className="auth-error" role="alert">
            <FiAlertCircle aria-hidden="true" />
            <span>{displayError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-field">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-hairline"
              placeholder="you@example.com"
              disabled={isLoading}
              autoComplete="email"
              required
            />
          </div>

          <div className="form-field">
            <label htmlFor="password" className="form-label">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-hairline"
              placeholder="Enter your password"
              disabled={isLoading}
              autoComplete="current-password"
              required
            />
          </div>

          <button type="submit" className="btn-ink btn-ink--block" disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="spinner" aria-hidden="true" />
                Signing in...
              </>
            ) : (
              <>
                Sign in →
                <span className="accent-dot" aria-hidden="true" />
              </>
            )}
          </button>
        </form>

        <div className="magic-link-row">
          <span className="magic-link-label">No password today?</span>
          <button
            type="button"
            className="btn-magic-link"
            onClick={handleMagicLink}
            disabled={!isEmailValid || isMagicLinkLoading}
          >
            {isMagicLinkLoading ? 'Sending...' : 'Send me a magic link'}
          </button>
        </div>

        {magicLinkSent && (
          <div className="magic-link-confirm" role="status">
            Check your inbox — link en route.
          </div>
        )}

        <div className="divider-or">
          <span className="divider-or__label">OR</span>
        </div>

        <button
          type="button"
          className="btn-google"
          disabled={isLoading}
          onClick={redirectToGoogleOAuth}
        >
          <GoogleIcon />
          Continue with Google
        </button>

        <div className="auth-page-footer">
          <p>New here? <Link to="/register">Start your front page →</Link></p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
